import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PrinterRegistrationModal from '../components/PrinterRegistrationModal';
import PrinterManagementModal from '../components/PrinterManagementModal';
import PrinterImage from '../components/PrinterImage';
import { FolderIconSVG, KeyIconSVG, SerialNumberIconSVG, NetworkIconSVG, USBIconSVG, CalendarIconSVG, ScanIconSVG, DesignerIconSVG, SettingsIconSVG } from '../components/DashboardIcons';
import './Dashboard.css';
import { getApiBaseUrl } from '../designer/config/api';

interface Printer {
  name: string;
  ip_address: string;
  is_online: boolean;
  serial_number?: string;
  firmware_version?: string;
  printer_name?: string; // Nombre de Windows para USB
  type?: 'Matica' | 'Other' | 'Unknown';
  verified?: boolean;
  verifying?: boolean;
}

interface RegisteredPrinter {
  id?: string;
  model: string;
  serial: string;
  ip: string;
  printerName?: string; // Nombre de Windows para USB
  distributorCode: string;
  dateRegistered: string;
}

import { DesignStorageService } from '../designer/utils/DesignStorageService';
import { DASHBOARD_VERSION } from './dashboard-version';

export default function Dashboard() {
  console.log('Dashboard v11 loaded - Saved Designs moved to bottom');
  console.log('Dashboard version:', DASHBOARD_VERSION);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [registeredPrinters, setRegisteredPrinters] = useState<RegisteredPrinter[]>([]);
  const [savedDesigns, setSavedDesigns] = useState<any[]>([]); // Nuevo estado
  const [loading, setLoading] = useState(false);
  const [serverUrl, setServerUrl] = useState(getApiBaseUrl());
  const [user, setUser] = useState({ name: '', role: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPrinterIp, setSelectedPrinterIp] = useState('');
  const [selectedPrinterSerial, setSelectedPrinterSerial] = useState('');
  const [selectedPrinterModel, setSelectedPrinterModel] = useState('');
  const [managementModalOpen, setManagementModalOpen] = useState(false);
  const [selectedPrinterForMgmt, setSelectedPrinterForMgmt] = useState<RegisteredPrinter | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const name = localStorage.getItem('userName') || 'User';
    const role = localStorage.getItem('userRole') || 'General';
    setUser({ name, role });

    // Cargar diseños guardados filtrados por accountId
    const accountId = localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID';
    const allDesigns = DesignStorageService.getAllDesigns(accountId);
    console.log('[Dashboard v12] Loaded designs:', allDesigns.length, 'for account:', accountId);
    setSavedDesigns(allDesigns);

    // Initial scan
    discoverPrinters();
  }, []);

  const handleOpenDesign = (id: string) => {
    navigate(`/designer?designId=${id}`);
  };

  const handleDeleteDesign = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('¿Estás seguro de eliminar este diseño?')) {
      const accountId = localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID';
      DesignStorageService.deleteDesign(id);
      setSavedDesigns(DesignStorageService.getAllDesigns(accountId));
    }
  };

  const handleExportDesign = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    DesignStorageService.exportDesignToFile(id);
  };

  const discoverPrinters = async () => {
    setLoading(true);
    // Don't clear immediately to avoid flickering
    // setPrinters([]); 
    // setRegisteredPrinters([]);

    try {
      // 1. Fetch Registered Printers (Inventory)
      const registeredResponse = await fetch(`${serverUrl}/api/printer/registered`);
      const registeredData = await registeredResponse.json();

      if (registeredData.Success && registeredData.Printers) {
        setRegisteredPrinters(registeredData.Printers.map((p: any) => ({
          id: p._id,
          model: p.Model,
          serial: p.SerialNumber,
          ip: p.IpAddress,
          printerName: p.Name,
          distributorCode: '',
          dateRegistered: p.RegisteredAt ? new Date(p.RegisteredAt).toLocaleDateString() : 'N/A'
        })));
      }

      // 2. Fetch Discovered Printers (New/Unknown)
      const discoverResponse = await fetch(`${serverUrl}/api/printer/discover`);
      const discoverData = await discoverResponse.json();

      if (discoverData.success && discoverData.printers) {
        const currentAccountId = localStorage.getItem('accountId');

        // Filter out printers that might be registered but somehow showed up here (safety check)
        // Also filter out printers that are already in the registered list to avoid duplicates
        const discovered = discoverData.printers.map((p: any) => ({
          ...p,
          type: (p.ip_address === 'USB' || (p.name && p.name.includes('Matica'))) ? 'Matica' : 'Unknown',
          verified: true,
          name: p.name || p.model || 'Unknown Device',
          ip_address: p.ip_address || '0.0.0.0',
          serial_number: p.serial_number || 'N/A',
          firmware_version: p.firmware_version,
          printer_name: p.printer_name,
          account_id: p.account_id,
          registration_date: p.registration_date
        })).filter((p: any) => {
          // Only show if NOT registered to current account (already handled above)
          // and NOT in the registered list we just fetched
          const isRegistered = registeredData.Printers?.some((rp: any) => rp.SerialNumber === p.serial_number);
          return !isRegistered;
        });

        setPrinters(discovered);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const verifyPrinter = async (ip: string, index: number) => {
    const newPrinters = [...printers];
    newPrinters[index].verifying = true;
    setPrinters(newPrinters);

    setTimeout(() => {
      const updatedPrinters = [...printers];
      updatedPrinters[index].verifying = false;
      updatedPrinters[index].verified = true;

      if (ip.includes('100.5')) {
        updatedPrinters[index].type = 'Matica';
        updatedPrinters[index].name = 'Matica MC210';
      } else {
        updatedPrinters[index].type = 'Unknown';
      }

      setPrinters(updatedPrinters);
    }, 1500);
  };

  const openRegistration = (printer: Printer) => {
    setSelectedPrinterIp(printer.ip_address);
    setSelectedPrinterSerial(printer.serial_number || '');
    const modelMatch = printer.name.match(/^([A-Z0-9]+)/);
    setSelectedPrinterModel(modelMatch ? modelMatch[1] : 'MC210');
    setIsModalOpen(true);
  };

  const handleRegisterSuccess = (data: { model: string; serial: string; ip: string; distributorCode: string }) => {
    discoverPrinters();
  };

  const openManagement = (printer: RegisteredPrinter) => {
    setSelectedPrinterForMgmt(printer);
    setManagementModalOpen(true);
  };

  const handlePrinterDeleted = (id: string) => {
    discoverPrinters();
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'Super Admin': return 'role-admin';
      case 'Supervisor': return 'role-supervisor';
      default: return 'role-general';
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-brand">
          <h2>Matica Manager</h2>
        </div>
        <div className="header-actions">
          <div className="user-profile">
            <span className="user-name">{user.name}</span>
            <span className={`user-role ${getRoleBadgeClass(user.role)}`}>{user.role}</span>
          </div>
          <button onClick={() => navigate('/')} className="btn-secondary">Logout</button>
        </div>
      </header>

      <div className="content-wrapper">
        {user.role === 'Super Admin' && (
          <div className="config-panel">
            <label>Bridge Server URL (Admin Only): </label>
            <input value={serverUrl} onChange={(e) => setServerUrl(e.target.value)} />
          </div>
        )}

        <div className="account-id-panel">
          <div className="account-id-header">
            <h3>
              <KeyIconSVG className="inline-icon" /> Your Account ID
            </h3>
            <span>Use this ID to link your Printer Client</span>
          </div>
          <div className="account-id-display">
            {localStorage.getItem('accountId') || 'MATICA-DEMO-ACCOUNT-ID'}
          </div>
        </div>

        <div className="left-column-content">
          {registeredPrinters.length > 0 && (
            <div className="registered-section">
              <h3><FolderIconSVG className="inline-icon" /> Registered Inventory</h3>
              <div className="registered-grid">
                {registeredPrinters.map((rp, i) => (
                  <div key={i} className="registered-card">
                    <div className="rp-icon">
                      <PrinterImage model={rp.model} size="medium" />
                    </div>
                    <div className="rp-info">
                      <h4>{rp.model}</h4>
                      <div className="rp-details-grid">
                        <div className="rp-detail">
                          <SerialNumberIconSVG className="detail-icon" />
                          {rp.serial}
                        </div>
                        <div className="rp-detail">
                          {rp.ip === 'USB' || rp.ip.includes('USB') ? (
                            <><USBIconSVG className="detail-icon" /> USB</>
                          ) : (
                            <><NetworkIconSVG className="detail-icon" /> {rp.ip}</>
                          )}
                        </div>
                        <div className="rp-detail" style={{ gridColumn: '1 / -1' }}>
                          <CalendarIconSVG className="detail-icon" /> Reg: {rp.dateRegistered}
                        </div>
                      </div>
                      <button
                        onClick={() => openManagement(rp)}
                        className="btn-manage"
                      >
                        <SettingsIconSVG className="btn-icon" /> Manage
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


        </div>

        <div className="action-bar">
          <button onClick={discoverPrinters} disabled={loading} className="btn-primary large">
            <ScanIconSVG className="btn-icon" />
            {loading ? 'Scanning...' : 'Scan for Printers'}
          </button>
          <button onClick={() => navigate('/designer')} className="btn-primary large" style={{ background: 'linear-gradient(to right, #4f46e5, #3b82f6)' }}>
            <DesignerIconSVG className="btn-icon" />
            Card Designer
          </button>
        </div>

        <div className="printer-list-section">
          <h3>Discovered Devices</h3>
          {printers.length === 0 && !loading && <div className="empty-state">No devices found. Click Scan to start.</div>}

          <div className="printer-grid">
            {printers.map((p, i) => (
              <div key={i} className={`printer-card ${p.type === 'Matica' ? 'verified-matica' : ''}`}>
                <div className="printer-icon">
                  {p.type === 'Matica' ? '🖨️' : '❓'}
                </div>
                <div className="printer-details">
                  <h4>{p.name}</h4>
                  <div className="ip-address">{p.ip_address}</div>
                  {p.serial_number && (
                    <div className="serial-number">SN: {p.serial_number}</div>
                  )}
                  {p.firmware_version && (
                    <div className="firmware-version">FW: {p.firmware_version}</div>
                  )}
                  <div className={`status-indicator ${p.is_online ? 'online' : 'offline'}`}>
                    {p.is_online ? 'Online' : 'Offline'}
                  </div>

                  <div className="verification-status">
                    {p.verified ? (
                      <span className={`badge ${p.type?.toLowerCase()}`}>
                        {p.type === 'Matica' ? '✅ Verified Matica' : `⚠️ ${p.type}`}
                      </span>
                    ) : (
                      <button onClick={() => verifyPrinter(p.ip_address, i)} className="btn-verify">
                        {p.verifying ? 'Checking...' : 'Verify Identity'}
                      </button>
                    )}
                  </div>

                  {p.verified && p.type === 'Matica' && (
                    <div className="admin-actions">
                      <button onClick={() => openRegistration(p)} className="btn-register">
                        Register Printer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {savedDesigns.length > 0 && (
          <div className="saved-designs-section-v2" data-version="v12-bottom-section" style={{ backgroundColor: 'rgba(30, 41, 59, 0.3)', padding: '2rem', borderRadius: '12px' }}>
            <h3><span className="inline-icon">📂</span> Mis Diseños Guardados</h3>
            <div className="registered-grid">
              {savedDesigns.map((d) => (
                <div key={d.id} className="registered-card design-card" onClick={() => handleOpenDesign(d.id)} style={{ cursor: 'pointer' }}>
                  <div className="rp-icon" style={{ padding: 0, overflow: 'hidden', background: '#fff' }}>
                    <img src={d.thumbnail} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <div className="rp-info">
                    <h4>{d.name}</h4>
                    <div className="rp-details-grid">
                      <div className="rp-detail" style={{ gridColumn: '1 / -1' }}>
                        📅 {new Date(d.updatedAt).toLocaleDateString()}
                      </div>
                      {d.databaseConfig && (
                        <div className="rp-detail" style={{ gridColumn: '1 / -1', color: '#60a5fa' }}>
                          🗄️ {(d.databaseConfig.type || 'DATASET').toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button onClick={(e) => handleExportDesign(d.id, e)} className="btn-manage" style={{ flex: 1, justifyContent: 'center' }} title="Exportar .db">
                        ⬇️
                      </button>
                      <button onClick={(e) => handleDeleteDesign(d.id, e)} className="btn-manage" style={{ flex: 1, background: '#ef4444', justifyContent: 'center' }} title="Eliminar">
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <PrinterRegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        printerIp={selectedPrinterIp}
        printerSerial={selectedPrinterSerial}
        printerModel={selectedPrinterModel}
        isAdmin={user.role === 'Super Admin'}
        onRegisterSuccess={handleRegisterSuccess}
      />

      {selectedPrinterForMgmt && (
        <PrinterManagementModal
          isOpen={managementModalOpen}
          onClose={() => setManagementModalOpen(false)}
          printer={{
            name: selectedPrinterForMgmt.printerName || selectedPrinterForMgmt.model,
            ip_address: selectedPrinterForMgmt.ip,
            serial_number: selectedPrinterForMgmt.serial,
            firmware_version: ''
          }}
          serverUrl={serverUrl}
          onDelete={handlePrinterDeleted}
        />
      )}
    </div>
  );
}
